import { Injectable } from '@angular/core'
import { merge, Observable, Subscription } from 'rxjs'
import { filter, map, share, switchMap } from 'rxjs/operators'
import { AppConfigService } from '../app-config/app-config.service'
import { LicenseInfoItem, ServerInfo } from '../models/app-config/server-info.model'
import { SettingsService } from '../modules/settings'
import DateTimeUtils from '../utils/date-time.utils'
import { NotificationService } from './notification.service'

/** Checks license infos received from be and offers method to check license and show warning, if license will expire within an adjustable (via settings) period. */
@Injectable({
  providedIn: 'root'
})
export class LicenseService {
  private userLoggedInSubscription: Subscription
  constructor(
    private settingsService: SettingsService,
    private appConfig: AppConfigService,

    private notificationService: NotificationService
  ) {}

  /** read the expiration-date value from  license-info array */
  private getDaysUntilExpired(licenseInfos: LicenseInfoItem[]): number {
    const expirationDateStr = licenseInfos?.find((li) => li.field === 'Expiration Date').value
    const expirationDate = new Date(expirationDateStr)
    const result = DateTimeUtils.getDiffInDays(expirationDate, new Date())
    return result
  }

  /** checks, whether the licence-info array contains an 'Expiration Date'-entry  */
  private hasExpirationDate(licenseInfos: LicenseInfoItem[]): boolean {
    const hasExpDate = licenseInfos?.some((li) => {
      const hasFieldAndFieldHasValue = li.field === 'Expiration Date' && !!li.value
      return hasFieldAndFieldHasValue
    })
    return hasExpDate
  }

  /** read the customer name from  license-info array */
  private getCustomerInfo(licenseInfos: LicenseInfoItem[]): string {
    const result = licenseInfos?.find((li) => li.field === 'Customer')?.value
    return result
  }

  /** Expects an observable that fires at login. it will then check, if the license is about to expire, in which case a warning notification is triggered for the user.  */
  checkExpirationDateAndShowWarningMessage(userLogsInEvent$: Observable<any>): void {
    // unsubscribe from previous subscription, if existing.
    if (!!this.userLoggedInSubscription) {
      this.userLoggedInSubscription.unsubscribe()
    }

    // check expiration info
    this.userLoggedInSubscription = userLogsInEvent$
      .pipe(switchMap(() => this.getExpirationInfo$()))
      .subscribe((result) => {
        if (!result.isAboutToExpire) {
          return
        }

        // notify
        const customerInfo = !!result.customer ? ' für ' + result.customer : ''
        this.notificationService.addWarnNotification(
          `Achtung! Lizenz${customerInfo} läuft in ${result.daysUntilExpired} Tagen ab!`
        )
      })
  }

  /** Checks the serverInfo for license information and the LICENSE_EXPIRE_WARNING settings to calculate, whether the license is about to expire within the defined time frame. */
  getExpirationInfo$(): Observable<{
    isAboutToExpire: boolean
    daysUntilExpired: number
    customer: string
  }> {
    /* 
    - check, if license info is set
    - if not, return true
    - if set:
    - get expirationWarningTime from setting
    - if value is set, take value, take 14 otherwise
    - (don't run check, if license is already expired)
    - expiration date - now <= warning
    - if yes --> return false
    - if no --> return true

     */

    // share to prevent multiple subscriptions to serverInfo observable
    const serverInfo$ = this.appConfig.getServerInfo$().pipe(share())

    // retrieve server info and check for expiration date.
    const serverInfoAndExpirationDateInfo$: Observable<{ hasExpDate: boolean; serverInfo: ServerInfo }> =
      serverInfo$.pipe(
        map((serverInfo) => {
          const result = {
            hasExpDate: this.hasExpirationDate(serverInfo.licenseInfos),
            serverInfo: serverInfo
          }
          return result
        })
      )

    // this observable fires a serverInfo only, if the license has an expiration date defined.
    const serverInfoHasExpirationDate$: Observable<ServerInfo> = serverInfoAndExpirationDateInfo$.pipe(
      filter(({ hasExpDate }) => hasExpDate),
      map(({ serverInfo }) => serverInfo)
    )

    // this observable fires a serverInfo only, if the license has NO expiration date defined.
    const serverInfoHasNoExpirationDate$: Observable<any> = serverInfoAndExpirationDateInfo$.pipe(
      filter(({ hasExpDate }) => !hasExpDate)
      // no map necessary, since value will not be processed.
    )

    const customer$: Observable<string> = serverInfo$.pipe(
      map((serverInfo) => this.getCustomerInfo(serverInfo.licenseInfos))
    )

    // combining both disjunct observables to one result observable
    const result$ = merge(
      // when expiration date is defined, it has to be checked
      serverInfoHasExpirationDate$.pipe(
        map((serverInfo) => this.getDaysUntilExpired(serverInfo.licenseInfos)),
        switchMap((daysUntilExpired) =>
          this.settingsService.getSetting('LICENSE_EXPIRE_WARNING').pipe(
            // convert string to number
            map((settingItem) => +settingItem.value),
            // get customer name
            switchMap((licenseExpireWarningInDays) => customer$.pipe(map((x) => [licenseExpireWarningInDays, x]))),
            // calculating, whether the license is about to expire within the (in LICENSE_EXPIRE_WARNING) defined time frame.
            map(([licenseExpireWarningInDays, customer]: [number, string]) => {
              const isAboutToExpire = daysUntilExpired <= licenseExpireWarningInDays
              return {
                isAboutToExpire,
                daysUntilExpired,
                customer
              }
            })
          )
        )
      ),

      // when no expiration date is set, we just return a default result.
      serverInfoHasNoExpirationDate$.pipe(map(() => ({ isAboutToExpire: false, daysUntilExpired: 0, customer: null })))
    )

    return result$
  }
}
